import React, { ReactNode } from 'react';
import { BreadcrumbProps } from '../../components/Breadcrumb';
import { Image } from '../../components/Image';
import { Tag, TagProps } from '../../components/Tag';
import { AuthorCard } from '../../layouts/AuthorCard';
import { textBody, textSerifTitle2 } from '../../styles/typography';
import { PayFitColor } from '../../types/module';
import { classNames } from '../../utils/style';
import { applyBgLightColor, useScreenSize } from '../../utils/utils';

import { CalloutCta } from '../../components/CalloutCta';
import { container } from '../../styles/themes';
import { ImageProps } from '../../components/Image/types';

export interface HeroBlogProps {
  title: string;
  subtitle?: string;
  backgroundColor?: PayFitColor;
  authorImage?: ImageProps;
  authorName?: string;
  authorPosition?: string;
  updateTime?: Date;
  readingTime?: number; // seconds
  image?: ImageProps;
  tags?: TagProps[];
  breadcrumbs?: BreadcrumbProps;
  cta?: ReactNode;
  ctaTitle?: string;
  locale?: 'fr' | 'en' | 'es' | undefined;
  isCentered?: boolean;
  className?: string;
  titleClassName?: string;
}

export const HeroBlog = ({
  title,
  subtitle,
  backgroundColor = 'blue',
  authorImage,
  authorName,
  authorPosition,
  updateTime,
  readingTime,
  image,
  tags,
  cta,
  ctaTitle,
  locale,
  isCentered,
  className,
  titleClassName,
}: HeroBlogProps) => {
  const screenSize = useScreenSize();
  const isMobile = ['xs', 'sm', 'md'].includes(screenSize);
  return (
    <div
      className={classNames(
        'HeroBlog',
        applyBgLightColor(backgroundColor),
        'blog-hero overflow-hidden',
        container,
        'rounded-[24px] mx-16 md:mx-24 lg:mx-auto',
        className,
      )}
    >
      <div
        className={classNames(
          !isCentered && 'lg:grid lg:grid-cols-12 lg:grid-rows-1',
        )}
      >
        <div
          className={classNames(
            'px-20 py-24 lg:py-40 lg:pr-40px lg:pl-64 lg:col-span-8 lg:col-start-1',
            isCentered &&
              'flex flex-col items-center justify-center min-h-[200px] gap-16 lg:px-64',
          )}
        >
          {tags && tags.length > 0 && (
            <div className="flex flex-row gap-8 mb-16 flex-wrap">
              {tags.map((tag: any, i: any) => (
                <Tag {...tag} key={i}>{tag.title}</Tag>
              ))}
            </div>
          )}
          <h1
            className={classNames(
              textSerifTitle2,
              'text-neutral-301',
              !isCentered && 'w-full',
              titleClassName,
            )}
          >
            {title}
          </h1>
          {subtitle && (
            <p
              className={classNames(
                textBody,
                'text-neutral-101 text-center',
                !isCentered && 'w-full',
              )}
            >
              {subtitle}
            </p>
          )}
          {authorName && (
            <div className="mt-12 py-12">
              <AuthorCard
                className="flex-wrap"
                locale={locale}
                authorImage={authorImage}
                authorName={authorName}
                authorPosition={authorPosition}
                updateTime={updateTime}
                readingTime={readingTime}
              />
            </div>
          )}
          {cta && !isMobile && (
            <div className="mt-12 hidden lg:block ">
              <CalloutCta title={ctaTitle} cta={cta}></CalloutCta>
            </div>
          )}
        </div>
        {image && (
          <div className="hidden lg:block lg:col-start-9 lg:col-span-5">
            <Image {...image} className="h-full" sizes={['333px']} />
          </div>
        )}
      </div>
    </div>
  );
};
